// Plant pricing calculator: types, API client, and React hook
// Updated: margins are stored and previewed as percentages applied to (purchase + logistics per plant)

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';

// --- Types ---

export interface PlantPricingRequest {
  variantId: string;
  quantity: number;
  storeCalculation?: boolean;
  previewOverrides?: {
    purchasePriceCents?: number;
    plantsPerBox?: number;
    boxesPerPalletPosition?: number;
    boxCostCents?: number;
    marginPercentage?: number;
    roundingIncrementCents?: number;
  };
}

export interface PlantPricingResponse {
  input: {
    variantId: string;
    productName: string;
    variantSize: string;
    variantType: string;
    quantity: number;
  };
  logistics: {
    plantsPerBox: number;
    boxesRequired: number;
    boxesPerPalletPosition: number;
    palletPositions: number;
    boxCostCents: number;
    totalBoxCostCents: number;
    transportCostCents: number;
    totalLogisticsCents: number;
    logisticsPerPlantCents: number;
  };
  pricing: {
    purchasePriceCents: number;
    marginPercentage: number;
    marginPerPlantCents: number;
    marginTierId: string | null;
    rawSellingPriceCents: number;
    roundingIncrementCents: number;
    sellingPriceCents: number;
    btwPercentage: number;
    sellingPriceInclVatCents: number;
  };
  transportPolicy: {
    freeTransportThresholdCents: number;
    transportContributionCents: number;
    orderValueInclVatCents: number;
    qualifiesForFreeTransport: boolean;
  };
  totals: {
    totalRevenueCents: number;
    totalPurchaseCents: number;
    totalLogisticsCents: number;
    totalCostCents: number;
    profitCents: number;
    profitMarginPercent: number;
  };
  requiresManualQuote: boolean;
}

export interface PlantPricingError {
  error: string;
  message: string;
}

// --- Pure utility functions (reusable for tests and local calculations) ---

export function roundUpToIncrementCents(valueCents: number, incrementCents: number): number {
  if (incrementCents <= 0) return Math.ceil(valueCents);
  return Math.ceil(valueCents / incrementCents) * incrementCents;
}

export function calculateBoxesRequired(quantity: number, plantsPerBox: number): number {
  if (plantsPerBox <= 0) throw new Error('plantsPerBox must be positive');
  return Math.ceil(quantity / plantsPerBox);
}

export function calculatePalletPositions(boxes: number, boxesPerPalletPosition: number): number {
  if (boxesPerPalletPosition <= 0) throw new Error('boxesPerPalletPosition must be positive');
  return Math.ceil(boxes / boxesPerPalletPosition);
}

export function calculateSellingPriceCents(
  purchasePriceCents: number,
  marginPerPlantCents: number,
  logisticsPerPlantCents: number,
  roundingIncrementCents: number
): number {
  const raw = purchasePriceCents + marginPerPlantCents + logisticsPerPlantCents;
  return roundUpToIncrementCents(raw, roundingIncrementCents);
}

export function calculateVatCents(priceCents: number, btwPercentage: number): number {
  return Math.round(priceCents * (btwPercentage / 100));
}

export function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function formatEuroCents(cents: number): string {
  return `\u20AC${centsToEuros(cents)}`;
}

// --- API Client ---

export async function calculatePlantPrice(
  request: PlantPricingRequest
): Promise<PlantPricingResponse> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${anonKey}`,
    'apikey': anonKey,
  };

  if (request.previewOverrides) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/calculate-plant-price`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as PlantPricingError;
  }

  return data as PlantPricingResponse;
}

// --- React Hook ---

interface UsePlantPriceCalculationOptions {
  debounceMs?: number;
}

interface UsePlantPriceCalculationResult {
  data: PlantPricingResponse | null;
  error: PlantPricingError | null;
  isLoading: boolean;
  requiresManualQuote: boolean;
  calculate: (request: PlantPricingRequest) => void;
}

export function usePlantPriceCalculation(
  options: UsePlantPriceCalculationOptions = {}
): UsePlantPriceCalculationResult {
  const { debounceMs = 300 } = options;
  const [data, setData] = useState<PlantPricingResponse | null>(null);
  const [error, setError] = useState<PlantPricingError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculate = useCallback((request: PlantPricingRequest) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    timerRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      abortRef.current = new AbortController();

      try {
        const result = await calculatePlantPrice(request);
        if (!abortRef.current?.signal.aborted) {
          setData(result);
          setError(null);
        }
      } catch (err: any) {
        if (!abortRef.current?.signal.aborted) {
          setData(null);
          setError(err?.error ? err : { error: 'UNKNOWN', message: err?.message || 'Unknown error' });
        }
      } finally {
        if (!abortRef.current?.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return {
    data,
    error,
    isLoading,
    requiresManualQuote: data?.requiresManualQuote ?? false,
    calculate,
  };
}
